import type { EmptyResponse, PlainObject, Proxy, Storage, StringObject, StringOrStringObject } from './types';
import { ProxyMethod, ProxyType } from './types/enums';
import ProxyService from './Proxy';
import Utils from './Utils';
import config from './config';

export default class StorageService {
  public proxy: ProxyService;
  readonly route = config.urls.blobs;

  constructor(proxy: ProxyService) {
    this.proxy = proxy;
  }

  public async list(params: Storage.ListParams = {}): Promise<Storage.ListResponse> {
    const ajaxParams = {
      type: ProxyMethod.GET,
      url: Utils.getUrl(this.route),
      data: params,
    };

    return this.proxy.ajax(ajaxParams);
  }

  public async create(params?: Storage.CreateParams): Promise<Storage.BlobResponse> {
    const ajaxParams = {
      type: ProxyMethod.POST,
      url: Utils.getUrl(this.route),
      data: { blob: params },
    };

    return this.proxy.ajax(ajaxParams);
  }

  public async delete(id: number | string): Promise<EmptyResponse> {
    const ajaxParams = {
      type: ProxyMethod.DELETE,
      url: Utils.getUrl(this.route, id),
      dataType: ProxyType.TEXT,
    };

    return this.proxy.ajax(ajaxParams);
  }

  public async createAndUpload(params: Storage.CreateAndUploadParams): Promise<Storage.Blob> {
    const { name, file, size, abort_id, type: content_type, public: isPublic = true } = params;
    const createParams = { name, content_type, public: isPublic };

    let blob: Storage.Blob;

    return this.create(createParams)
      .then(({ blob: blobObject }) => {
        blob = blobObject;
        const uri = this.parseUri(blob.blob_object_access?.params);
        const uploadUrl = `${uri.protocol}://${uri.authority}${uri.path}`;
        const uploadParams: Proxy.Params = { url: uploadUrl, data: {} };

        if (abort_id) {
          uploadParams.abort_id = abort_id;
        }

        const queryKey = typeof uri.queryKey === 'object' && uri.queryKey !== null ? uri.queryKey : {};
        const data = (uploadParams.data ?? {}) as PlainObject;

        Object.keys(queryKey).forEach((key) => {
          data[key] = decodeURIComponent(queryKey[key]);
        });
        data.file = file;
        uploadParams.data = data;

        return this.upload(uploadParams).then(() => blob);
      })
      .then((blob) => this.markUploaded({ id: blob.id, size }).then(() => blob))
      .then((blob) => ({ ...blob, size }));
  }

  public async upload(params: Proxy.Params): Promise<any> {
    const ajaxParams = {
      ...params,
      type: ProxyMethod.POST,
      dataType: ProxyType.TEXT,
      contentType: false,
    };

    return this.proxy.ajax(ajaxParams);
  }

  public async markUploaded(params: Storage.MarkUploadedParams): Promise<EmptyResponse> {
    const ajaxParams = {
      type: ProxyMethod.PUT,
      url: Utils.getUrl(this.route, params.id, 'complete'),
      data: {
        size: params.size,
      },
      dataType: ProxyType.TEXT,
    };

    return this.proxy.ajax(ajaxParams);
  }

  public async getInfo(id: number | string): Promise<Storage.BlobResponse> {
    const ajaxParams = {
      url: Utils.getUrl(this.route, id),
    };

    return this.proxy.ajax(ajaxParams);
  }

  public async getFile(uid: string): Promise<Storage.BlobResponse> {
    const ajaxParams = {
      url: Utils.getUrl(this.route, uid),
    };

    return this.proxy.ajax(ajaxParams);
  }

  public async getFileObject(uid: string, params: Storage.GetFileObjectParams): Promise<Storage.BlobResponse> {
    const ajaxParams = {
      type: ProxyMethod.GET,
      url: Utils.getUrl(this.route, uid, 'object'),
      data: params,
    };

    return this.proxy.ajax(ajaxParams);
  }

  public async update(params: Storage.UpdateParams): Promise<Storage.BlobResponse> {
    const data = { blob: {} as PlainObject };

    if (typeof params.name !== 'undefined') {
      data.blob.name = params.name;
    }

    const ajaxParams = {
      url: Utils.getUrl(this.route, params.id),
      data: data,
    };

    return this.proxy.ajax(ajaxParams);
  }

  public privateUrl(fileUID: string = ''): string {
    return `${config.endpoints.api_url ?? `https://${config.endpoints.api}`}/blobs/${fileUID}?token=${this.proxy.getSession()?.token}`;
  }

  public publicUrl(fileUID: string = ''): string {
    return `${config.endpoints.api_url ?? `https://${config.endpoints.api}`}/blobs/${fileUID}`;
  }

  private parseUri(str: string = ''): Record<string, StringOrStringObject> {
    const options = {
      strictMode: false,
      key: [
        'source',
        'protocol',
        'authority',
        'userInfo',
        'user',
        'password',
        'host',
        'port',
        'relative',
        'path',
        'directory',
        'file',
        'query',
        'anchor',
      ] as const,
      q: {
        name: 'queryKey',
        parser: /(?:^|&)([^&=]*)=?([^&]*)/g,
      },
      parser: {
        strict:
          /^(?:([^:\/?#]+):)?(?:\/\/((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:\/?#]*)(?::(\d*))?))?((((?:[^?#\/]*\/)*)([^?#]*))(?:\?([^#]*))?(?:#(.*))?)/,
        loose:
          /^(?:(?![^:@]+:[^:@\/]*@)([^:\/?#.]+):)?(?:\/\/)?((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:\/?#]*)(?::(\d*))?)(((\/(?:[^?#](?![^?#\/]*\.[^?#\/.]+(?:[?#]|$)))*\/?)?([^?#\/]*))(?:\?([^#]*))?(?:#(.*))?)/,
      },
    };

    const m = options.parser[options.strictMode ? 'strict' : 'loose'].exec(str) ?? [];

    const uri = {
      source: '',
      protocol: '',
      authority: '',
      userInfo: '',
      user: '',
      password: '',
      host: '',
      port: '',
      relative: '',
      path: '',
      directory: '',
      file: '',
      query: '',
      anchor: '',
      queryKey: {} as StringObject,
    };

    options.key.forEach((key, i) => {
      uri[key] = m[i] ?? '';
    });

    uri.query.replace(options.q.parser, (_0: string, k: string, v: string) => {
      if (k) uri.queryKey[k] = v;
      return '';
    });

    return uri;
  }
}
